import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  FileText,
  QrCode,
  Zap,
  Loader2,
  XCircle,
  CheckCircle,
  Calendar,
  CreditCard,
  RotateCcw,
  ArrowUpDown,
  Info,
} from 'lucide-react';
import { BusinessDetails } from '@/types/index';
import { MonthStatus, PayrollPeriod } from '@/types/payroll-periods';
import { formatEUR } from '@/lib/payroll';
import { toast } from '@/hooks/ui/use-toast';
import { InlinePayrollQRCode } from './InlinePayrollQRCode';
import { PaymentStageId } from './payments/types';
import { cn } from '@/lib/core/utils';
import { EmployeePlanFulfillment } from '@/hooks/employees/usePlanFulfillment';
import { EmployeePlanProgress } from '@/components/employees/EmployeePlanProgress';
import EmployeeDetailModal from '@/components/employees/EmployeeDetailModal';
import { Employee } from '@/types';

interface EmployeeWithPayroll {
  employee_id: string;
  employee_name: string;
  role_name?: string;
  pay_rate: number;
  actual_hours: number;
  gross_pay: number;
  net_pay: number;
  calculation_method: string;
  country_code: string | null;
  auto_calculated: boolean;
  status: string;
  has_payroll_record: boolean;
  payroll_record_id?: string;
  created_at?: string;
}

interface EmployeePayrollTableProps {
  employees: EmployeeWithPayroll[];
  fulfillmentData: Map<string, EmployeePlanFulfillment>;
  currentMonthStatus?: MonthStatus | null;
  selectedPeriod?: PayrollPeriod | null;
  business: BusinessDetails | null;
  onPayslipGenerate: (employee: EmployeeWithPayroll) => void;
  getPaymentAvailability: (employeeId: string) => {
    qrCodesAvailable: boolean;
    readOnlyMode: boolean;
    userFriendlyState: 'FUTURE' | 'UPCOMING' | 'IN_PROGRESS' | 'TO_BE_PROCESSED' | 'PROCESSED';
  };
  getPaymentData: (employeeId: string) => {
    stage?: PaymentStageId;
    stripePaymentId?: string;
    failureReason?: string;
  } | undefined;
  onRetryPayment: (employee: EmployeeWithPayroll) => void;
  onProcessPayment: (employee: EmployeeWithPayroll) => void;
  stripeEnabled: boolean;
  processingEmployeeId?: string;
  onBulkSelect?: (employeeIds: string[]) => void;
  selectedEmployeeIds?: string[];
}

// Payment stage configuration
const PAYMENT_STAGE_CONFIG: Record<PaymentStageId, {
  label: string;
  icon: React.ElementType;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
}> = {
  upcoming: {
    label: 'Upcoming',
    icon: Calendar,
    variant: 'secondary',
  },
  ready: {
    label: 'Ready',
    icon: CreditCard,
    variant: 'outline',
  },
  processing: {
    label: 'Processing',
    icon: Loader2,
    variant: 'secondary',
  },
  completed: {
    label: 'Paid',
    icon: CheckCircle,
    variant: 'default',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    variant: 'destructive',
  },
};

type SortField = 'name' | 'gross' | 'net' | 'status';
type SortDirection = 'asc' | 'desc';

export const EmployeePayrollTable: React.FC<EmployeePayrollTableProps> = ({
  employees,
  fulfillmentData,
  currentMonthStatus,
  selectedPeriod,
  business,
  onPayslipGenerate,
  getPaymentAvailability,
  getPaymentData,
  onRetryPayment,
  onProcessPayment,
  stripeEnabled,
  processingEmployeeId,
  onBulkSelect,
  selectedEmployeeIds = [],
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [generatingPayslipFor, setGeneratingPayslipFor] = useState<string | null>(null);
  const [selectedEmployeeForModal, setSelectedEmployeeForModal] = useState<EmployeeWithPayroll | null>(null);

  // Determine if business uses Euro
  const useEuro = business?.country_code === 'SK' || business?.country_code === 'CZ' || business?.country_code === 'AT';
  const formatCurrency = (amount: number) => useEuro ? formatEUR(amount) : `$${amount.toFixed(2)}`;

  // Get employee initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Toggle row expansion
  const toggleRowExpansion = (employeeId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  // Handle payslip generation
  const handlePayslipGenerate = async (employee: EmployeeWithPayroll) => {
    if (!employee.has_payroll_record) {
      toast.error('Payslip not available', {
        description: 'This employee does not have payroll records for this period.'
      });
      return;
    }

    setGeneratingPayslipFor(employee.employee_id);
    try {
      await onPayslipGenerate(employee);
    } catch (error) {
      toast.error('Failed to generate payslip');
    } finally {
      setGeneratingPayslipFor(null);
    }
  };

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedEmployees = [...employees].sort((a, b) => {
    const fulfillmentA = fulfillmentData.get(a.employee_id);
    const fulfillmentB = fulfillmentData.get(b.employee_id);

    const getDisplayGross = (emp: EmployeeWithPayroll, ful?: EmployeePlanFulfillment) =>
      emp.has_payroll_record ? emp.gross_pay : (ful?.hoursWorked || 0) * emp.pay_rate;

    const getDisplayNet = (emp: EmployeeWithPayroll, ful?: EmployeePlanFulfillment) =>
      emp.has_payroll_record ? emp.net_pay : getDisplayGross(emp, ful) * 0.75;

    let comparison = 0;
    switch (sortField) {
      case 'name':
        comparison = a.employee_name.localeCompare(b.employee_name);
        break;
      case 'gross':
        comparison = getDisplayGross(a, fulfillmentA) - getDisplayGross(b, fulfillmentB);
        break;
      case 'net':
        comparison = getDisplayNet(a, fulfillmentA) - getDisplayNet(b, fulfillmentB);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Bulk selection
  const handleSelectAll = (checked: boolean) => {
    if (onBulkSelect) {
      onBulkSelect(checked ? employees.map(e => e.employee_id) : []);
    }
  };

  const handleSelectOne = (employeeId: string, checked: boolean) => {
    if (onBulkSelect) {
      if (checked) {
        onBulkSelect([...selectedEmployeeIds, employeeId]);
      } else {
        onBulkSelect(selectedEmployeeIds.filter(id => id !== employeeId));
      }
    }
  };

  const isAllSelected = employees.length > 0 && selectedEmployeeIds.length === employees.length;
  const isSomeSelected = selectedEmployeeIds.length > 0 && selectedEmployeeIds.length < employees.length;

  const SortHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => handleSort(field)}
    >
      {children}
      <ArrowUpDown className="ml-2 h-4 w-4" />
    </Button>
  );

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {onBulkSelect && (
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected}
                  // indeterminate state handled by shadcn
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
            )}
            <TableHead className="w-12"></TableHead>
            <TableHead>
              <SortHeader field="name">Employee</SortHeader>
            </TableHead>
            <TableHead>
              Plan Progress
            </TableHead>
            <TableHead className="text-right">
              <SortHeader field="gross">Gross</SortHeader>
            </TableHead>
            <TableHead className="text-right">
              <SortHeader field="net">Net Pay</SortHeader>
            </TableHead>
            <TableHead>
              <SortHeader field="status">Status</SortHeader>
            </TableHead>
            <TableHead className="w-20">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedEmployees.map((employee) => {
            const fulfillment = fulfillmentData.get(employee.employee_id);
            const paymentAvailability = getPaymentAvailability(employee.employee_id);
            const paymentData = getPaymentData(employee.employee_id);
            const isExpanded = expandedRows.has(employee.employee_id);
            const isProcessing = processingEmployeeId === employee.employee_id;
            const isGeneratingPayslip = generatingPayslipFor === employee.employee_id;

            // Use fulfillment data as fallback when no payroll record exists
            const displayGross = employee.has_payroll_record
              ? employee.gross_pay
              : (fulfillment?.hoursWorked || 0) * employee.pay_rate;

            const displayNet = employee.has_payroll_record
              ? employee.net_pay
              : displayGross * 0.75;

            return (
              <React.Fragment key={employee.employee_id}>
                <TableRow className={cn(
                  isExpanded && 'border-b-0'
                )}>
                  {onBulkSelect && (
                    <TableCell>
                      <Checkbox
                        checked={selectedEmployeeIds.includes(employee.employee_id)}
                        onCheckedChange={(checked) => handleSelectOne(employee.employee_id, !!checked)}
                        aria-label={`Select ${employee.employee_name}`}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => toggleRowExpansion(employee.employee_id)}
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={undefined} alt={employee.employee_name} />
                        <AvatarFallback className="bg-blue-100 text-blue-600 text-xs">
                          {getInitials(employee.employee_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="font-medium">{employee.employee_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {employee.role_name || 'No Role'} • {formatCurrency(employee.pay_rate)}/hr
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setSelectedEmployeeForModal(employee)}
                        title="View employee details"
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <EmployeePlanProgress fulfillment={fulfillment} compact={false} />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(displayGross)}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-green-600">
                    {formatCurrency(displayNet)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {paymentData?.stage && employee.has_payroll_record && (
                        (() => {
                          const stageConfig = PAYMENT_STAGE_CONFIG[paymentData.stage];
                          const StageIcon = stageConfig.icon;
                          return (
                            <Badge variant={stageConfig.variant} className="text-xs">
                              <StageIcon className={cn(
                                'h-3 w-3 mr-1',
                                paymentData.stage === 'processing' && 'animate-spin'
                              )} />
                              {stageConfig.label}
                            </Badge>
                          );
                        })()
                      )}
                      {!employee.has_payroll_record && (
                        <Badge variant="outline" className="text-xs">No Record</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handlePayslipGenerate(employee)}
                          disabled={!employee.has_payroll_record || isGeneratingPayslip}
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          {isGeneratingPayslip ? 'Generating...' : 'Generate Payslip'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => toggleRowExpansion(employee.employee_id)}
                          disabled={!paymentAvailability.qrCodesAvailable}
                        >
                          <QrCode className="mr-2 h-4 w-4" />
                          {isExpanded ? 'Hide QR Code' : 'Show QR Code'}
                        </DropdownMenuItem>
                        {stripeEnabled && paymentData?.stage === 'ready' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onProcessPayment(employee)}
                              disabled={isProcessing || !employee.has_payroll_record}
                            >
                              {isProcessing ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Zap className="mr-2 h-4 w-4" />
                              )}
                              {isProcessing ? 'Processing...' : 'Pay Now'}
                            </DropdownMenuItem>
                          </>
                        )}
                        {paymentData?.stage === 'failed' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onRetryPayment(employee)}
                              disabled={isProcessing}
                            >
                              <RotateCcw className="mr-2 h-4 w-4" />
                              Retry Payment
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
                {/* Expanded Row Content */}
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={onBulkSelect ? 7 : 6} className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* QR Code Section */}
                        {paymentAvailability.qrCodesAvailable && (
                          <div className="p-3 bg-background rounded-lg border">
                            <InlinePayrollQRCode
                              employee={employee}
                              business={business}
                              selectedPeriod={selectedPeriod}
                              userFriendlyState={paymentAvailability.userFriendlyState}
                            />
                          </div>
                        )}

                        {/* Payment Error */}
                        {paymentData?.stage === 'failed' && paymentData.failureReason && (
                          <div className="md:col-span-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <XCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm text-red-700 dark:text-red-300">
                                  <span className="font-medium">Payment failed:</span> {paymentData.failureReason}
                                </span>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onRetryPayment(employee)}
                                disabled={isProcessing}
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Retry
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Additional Info */}
                        <div className={cn(
                          "p-3 bg-background rounded-lg border",
                          !paymentAvailability.qrCodesAvailable && "md:col-span-2"
                        )}>
                          <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Details</div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Source:</span>
                              <span className="ml-2 font-medium">
                                {employee.has_payroll_record ? 'Payroll Record' : 'Time Tracking'}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Calculation:</span>
                              <span className="ml-2 font-medium">
                                {employee.auto_calculated ? 'Automatic' : 'Manual'}
                              </span>
                            </div>
                            {employee.country_code && (
                              <div>
                                <span className="text-muted-foreground">Country:</span>
                                <span className="ml-2 font-medium">{employee.country_code}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-muted-foreground">Status:</span>
                              <span className="ml-2 font-medium capitalize">{employee.status}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      {/* Employee Detail Modal */}
      <EmployeeDetailModal
        open={!!selectedEmployeeForModal}
        onOpenChange={(open) => !open && setSelectedEmployeeForModal(null)}
        employee={selectedEmployeeForModal ? {
          id: selectedEmployeeForModal.employee_id,
          business_id: business?.id || '',
          name: selectedEmployeeForModal.employee_name,
          email: '',
          created_at: selectedEmployeeForModal.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          hourly_rate: selectedEmployeeForModal.pay_rate,
          roleInfo: selectedEmployeeForModal.role_name ? {
            id: '',
            business_id: business?.id || '',
            name: selectedEmployeeForModal.role_name,
            hourly_rate: selectedEmployeeForModal.pay_rate,
            created_at: new Date().toISOString(),
          } : null,
        } as Employee : null}
        businessId={business?.id || null}
        defaultTab="payroll"
      />
    </div>
  );
};
